import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Avatar,
  Divider,
  Checkbox,
  FormControlLabel, Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  LineChart,
  Line, CartesianGrid,
  XAxis,
  YAxis,
  Tooltip, Area,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getDashboardEventSummary, getClientAudits, getClientCameras, getCameraCount, getChartCount } from 'src/api/clientDetails';
import { InsertUpdateEscStatus } from 'src/api/liveCameras';
import ListAudits from './ListAudits';
import InfoIcon from '@mui/icons-material/Info';
import { Tooltip as MuiTooltip } from '@mui/material';
import { getClientLocations, getClientProperties } from 'src/api/ShiftMaster';
import { siteTypeParam } from 'src/utils/siteType';
import useSiteType from 'src/hooks/useSiteType';
import { jsonToParam } from 'src/utils/config';
import { getCurrentClientNow, getMergeTimezoneIds, formatApiDateTime } from 'src/utils/clientTime';
import Page from '../components/Page';
import { format } from 'date-fns';
import { useTheme, alpha } from '@mui/material/styles';
import { getAppStorageItem } from 'src/utils/storageProxy';


const formatEventTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'MMM-dd-yyyy hh:mm a');
  } catch (error) {
    return 'Invalid Date';
  }
};

const getAssignedLocationsFromStorage = () => {
  try {
    const assignedLocationsRaw = getAppStorageItem('assigned_locations');
    if (!assignedLocationsRaw) return [];

    const parsedAssignedLocations = JSON.parse(assignedLocationsRaw);
    const assignedLocations = Array.isArray(parsedAssignedLocations)
      ? parsedAssignedLocations
      : [];

    return assignedLocations
      .map((locationItem) => ({
        ID: Number(locationItem?.ID ?? locationItem?.id ?? 0),
        Location: String(locationItem?.Location || locationItem?.name || '').trim(),
      }))
      .filter((locationItem) => locationItem.ID > 0);
  } catch (error) {
    console.error('Failed to read assigned_locations from storage:', error);
    return [];
  }
};

// Clients that open "View Events From" on a 30 day window instead of the
// default 24 hours.
const THIRTY_DAY_DEFAULT_CLIENT_IDS = [1438];

// const defaultTimeFilter = () =>
//   (THIRTY_DAY_DEFAULT_CLIENT_IDS.includes(Number(localStorage.getItem('client_id')))
//     ? 'last 30 days'
//     : '24 hrs');

const defaultTimeFilter = 'last 30 days';

export default function USClientsDashBoard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const UI = {
    panel: theme.palette.background.paper,
    surface: theme.palette.background.neutral,
    cardBg: isDark ? '#1a2638' : theme.palette.background.paper,
    cardBorder: isDark ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.text.primary, 0.16),
    textMain: theme.palette.text.primary,
    textMuted: theme.palette.text.secondary,
    accent: theme.palette.primary.main,
    accentText: theme.palette.primary.contrastText,
    accentBorder: alpha(theme.palette.primary.main, 0.5),
    shadow: isDark ? '0 8px 24px rgba(0, 0, 0, 0.28)' : '0 1px 3px rgba(0,0,0,0.08)',
    shadowHover: isDark ? '0 12px 28px rgba(0, 0, 0, 0.34)' : `0 4px 10px ${alpha(theme.palette.primary.main, 0.2)}`,
  };
  const navigate = useNavigate();
  const timeFilters = ['60 min', '24 hrs', 'last 7 days', 'last 30 days'];
  const [activeFilter, setActiveFilter] = useState(defaultTimeFilter);
  const [activeEventTypeIndex, setActiveEventTypeIndex] = useState(1);
  const [eventName, setEventName] = useState('')
  //const [isInitialRender,setIsInitialRender] = useState(true);
  const IsAgentBased = localStorage.getItem('IsAgentBased');
  const siteType = useSiteType();
  // the real client id: client_id below can hold the merge ID instead
  const siteTypeClientId = localStorage.getItem('client_id');
  let client_id = 0;
  if(localStorage.getItem('Type') === 'merge'){
    client_id = localStorage.getItem('ID');
  }
  else{
    client_id = localStorage.getItem('client_id');
  }
  
  const clientType = localStorage.getItem('client_type');
  const storedProperty = localStorage.getItem('Location');
  const AssignClient = localStorage.getItem('AssignClient');
  const Type = localStorage.getItem('Type');
  const userType = localStorage.getItem('user_type');

  const uniqueTimezoneIds = getMergeTimezoneIds();
  const [auditData, setAuditData] = useState([]);
  const [cameraInfo, setCameraInfo] = useState({ total:0, active: 0, inactive: 0 })
  const [topCameras, setTopCameraInfo] = useState([])
  const [recentEvents, setResentEvents] = useState([])
  const [escStatus, setEscStatus] = useState(true)
  const [confirmation, setConfirmation] = useState(false)
  const [isAllZero, setIsAllZero] = useState(false);
  const [chartData, setChartData] = useState([]);
  //const [displayChart,setDisplayChart] = useState(false)
  const cameraRequestRef = useRef(0);
  // Nothing may hit the dashboard APIs until the client's timezone has resolved,
  // otherwise the first payload carries browser-local dates.
  const [timezoneReady, setTimezoneReady] = useState(false);

  const [location, setLocation] = useState(() => {
    const stored = localStorage.getItem('selectedLocation');
    return stored ? parseInt(stored) : null;
  });
  const [locations, setLocations] = useState([]);
  
  const [property, setProperty] = useState(() => {
    const stored = localStorage.getItem('selectedProperty');
    return stored ? parseInt(stored) : null;
  });
  const [properties, setProperties] = useState([]);
  
  useEffect(() => {

    if(AssignClient!=='' && storedProperty!=='') {
      const assignedLocations = getAssignedLocationsFromStorage();
      setProperties(assignedLocations);

      const stored = localStorage.getItem('selectedProperty');
      if (stored) {
        const found = assignedLocations.find((loc) => loc.ID === parseInt(stored));
        if (found) {
          setProperty(found.ID);
        }
      } else {
        const first = assignedLocations[0];
        if (first) {
          setProperty(first.ID);
          localStorage.setItem('selectedProperty', first.ID);
        }
      }
    }
    
    const LocationBody = {client_id: Number(client_id)}
    getClientLocations(LocationBody, (response) => {
      if (response.status === 200) {
        setLocations(response.data);

        const stored = localStorage.getItem('selectedLocation');
        if (stored) {
          const found = response.data.find((loc) => loc.Id === parseInt(stored));
          if (found) {
            setLocation(found.Id);
          }
        } else {
          const first = response.data[0];
          if (first) {
            setLocation(first.Id);
            localStorage.setItem('selectedLocation', first.Id);
          }
        }
      }
    });
  }, []);


  // Provisional only. Both dates are replaced with client-timezone values as
  // soon as the timezone list resolves, and no API fires until it does.
  const [toDate, setToDate] = useState(() => dayjs().endOf('day'));
  const [fromDate, setFromDate] = useState(() => toDate.startOf('day'));

  // The window each time filter covers, always measured back from the client's
  // "now". Single definition -- initial load, filter clicks and refetches all
  // use it, so they cannot drift apart.
  const windowFromDate = (toDateValue, filter) => {
    switch (filter) {
      case '60 min':
        return toDateValue.subtract(60, 'minute');
      case '24 hrs':
        return toDateValue.subtract(1440, 'minute');
      case 'last 7 days':
        return toDateValue.subtract(7, 'day');
      case 'last 30 days':
        return toDateValue.subtract(30, 'day');
      default:
        return toDateValue.subtract(1440, 'minute');
    }
  };

  // Upper bound of the dashboard window, in the client's own timezone.
  // Returns a dayjs, sets no state -- callers own that.
  const getClientToDate = async () => {
    try {
      const clientNow = await getCurrentClientNow(clientType, uniqueTimezoneIds);

      if (IsAgentBased === '0' && client_id === '1118') {
        // Reporting day runs 5 AM to 5 AM; the bound is the next 5 AM boundary.
        const todayFiveAM = clientNow.hour(5).minute(0).second(0).millisecond(0);
        return clientNow.hour() < 5 ? todayFiveAM : todayFiveAM.add(1, 'day');
      }

      return clientNow.endOf('day');
    } catch (error) {
      console.error('Error resolving client timezone, falling back to browser time:', error);
      return dayjs().endOf('day');
    }
  };

  useEffect(() => {
    const requestId = ++cameraRequestRef.current;

    getClientToDate().then((clientToDate) => {
      setToDate(clientToDate);
      setFromDate(windowFromDate(clientToDate, activeFilter));
      setTimezoneReady(true);
    });

    let bodyParams = {};
    if(userType === "2") {
      bodyParams.status = '0,1';
      if (property !== null) {
        bodyParams.property_id = Number(property);
      } else if (!['share', 'sub', 'merge'].includes(Type)) {
        bodyParams.clientid = Number(client_id);
      }
    }

    bodyParams = jsonToParam(bodyParams);

    getClientCameras(bodyParams, (res) => {
      if (requestId !== cameraRequestRef.current) return;

      if (res.status === 200) {

        let camerasList = Array.isArray(res.data) ? res.data : [];
        console.log('camerasList:', camerasList);

        if (property !== null) {
          camerasList = camerasList.filter(
            (camera) =>
              Number(camera?.clientid ?? camera?.client_id ?? camera?.ClientId ?? 0) ===
              Number(property)
          );
        }

        if(client_id === "1316"){
          camerasList = camerasList.filter(
            (camera) => Number(camera.LocationId) === Number(location)
          );
        }

        const activeCameras = camerasList.filter(
          (camera) => Number(camera.CameraActiveStatus) === 1
        ).length;
        const inactiveCameras = camerasList.filter(
          (camera) => Number(camera.CameraActiveStatus) === 0
        ).length;

        setCameraInfo({
          total: camerasList,
          active: activeCameras,
          inactive: inactiveCameras
        })
      }
    })

  }, [location, property]);



  const handleTimeFilterClick = async (filter) => {
    setActiveFilter(filter);
    setActiveEventTypeIndex(0);

    const newToDate = await getClientToDate();
    setToDate(newToDate);
    setFromDate(windowFromDate(newToDate, filter));
    // const body = {
    //     fromdate:dayjs(newFromDate.format()).format('YYYY-MM-DD HH:mm:ss'),
    //     todate: dayjs(toDate).format('YYYY-MM-DD HH:mm:ss'),
    // };
    // // console.log(body)
    // getDashboardEventSummary(body, (response) => {
    //     if (response.status === 200) {
    //       setEventTypes(response.data || eventTypes);
    //     } else {
    //       console.error('API failed:', response);
    //     }
    // });
  };

  useEffect(() => {
    if (!timezoneReady) return;

    const newFromDate = windowFromDate(toDate, activeFilter);
    setFromDate(newFromDate);

    // Four independent calls make up the dashboard, so the page stays in its
    // loading state until the last one answers. `stale` drops the results of a
    // run the user has already replaced by picking another category or window.
    let stale = false;
    let pending = 4;
    const settle = () => {
      pending -= 1;
      if (pending === 0 && !stale) setDashboardLoading(false);
    };
    setDashboardLoading(true);

    // sent only for clients that have the navbar site type select
    const eventCategory = siteTypeParam(siteTypeClientId, siteType);

    let newData = {
      fromdate: formatApiDateTime(newFromDate),
      todate: formatApiDateTime(toDate),
      ...eventCategory,
    };

    if(client_id === "1316") {
      newData.location_id = Number(location);
    }

    if (property !== null) {
      newData.property_id = Number(property);
    }

    getDashboardEventSummary(newData, (response) => {
      if (stale) return settle();
      if (response.status === 200) {
        setEventTypes(Array.isArray(response.data) ? response.data : []);
      } else {
        console.error('API failed:', response);
      }
      settle();
    });

    getCameraCount(newData, (res) => {
      if (stale) return settle();
      const cameraCounts = Array.isArray(res?.data) ? res.data : [];
      setTopCameraInfo(res?.status === 200 ? cameraCounts : []);
      settle();
    });

    const type =
      activeFilter === 'last 7 days'
        ? '7days'
        : activeFilter === 'last 30 days'
          ? '30days'
          : activeFilter === '24 hrs'
            ? '24hours'
            : activeFilter === '60 min'
              ? '1hour'
              : '';

    const chartData = {
      type:type,
      ...eventCategory,
    }

    if(client_id === "1316") {
      chartData.location_id = Number(location);
    }

    if (property !== null) {
      chartData.property_id = Number(property);
    }

    getChartCount(chartData, (res) => {
      if (stale) return settle();
      if (res.status === 200 && res.data && res.data.previous && res.data.current) {
        let formattedData = [];
        if (activeFilter === '24 hrs') {

          formattedData = res.data.current.map((curr) => {
            const currTime = dayjs(curr.day).format('HH:mm');
            const prevData = res.data.previous.find(
              (prev) => dayjs(prev.day).format('HH:mm:ss') === dayjs(curr.day).format('HH:mm:ss')
            ) || { count: 0 };
            return {
              name: currTime + ' hr',
              Previous: prevData.count,
              Current: curr.count,
            };

          });
        } else if (activeFilter === 'last 7 days') {
          //const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          formattedData = res.data.previous.map((prev, index) => ({
            name: prev.day,
            Previous: prev.count,
            Current: res.data.current[index].count,
          }));


        } else if (activeFilter === 'last 30 days') {
          formattedData = res.data.current.map((curr, index) => {
            const currDate = dayjs(curr.day).format('MM-DD');
            const prevData = res.data.previous.find(
              (prev) => dayjs(prev.day).format('MM-DD') === currDate
            ) || { count: 0 };
            return {
              name: currDate,
              Previous: prevData.count,
              Current: curr.count,
            };
          });
        } else if (activeFilter === '60 min') {
          formattedData = res.data.current.map((curr) => {
            const currMinute = dayjs(curr.day).format('HH:mm'); // Extract minutes (e.g., '47', '57')
            // Find matching previous data by full timestamp (to handle potential misalignment)
            const prevData = res.data.previous.find(
              (prev) => dayjs(prev.day).format('HH:mm:ss') === dayjs(curr.day).format('HH:mm:ss')
            ) || { count: 0 };
            return {
              name: currMinute + ' min', // Use only the minute part for X-axis
              Previous: prevData.count,
              Current: curr.count,
            };
          });
        }

        const allNonZero = (data) => {
          return data.every(item => item.Previous == 0 && item.Current == 0);
        };
        const result = allNonZero(formattedData)
        if (result) {
          setIsAllZero(result);
          formattedData = formattedData.map((item, index) => ({
            ...item,
            Previous: 0.05,
            Current: 0,
          }));
        }
        setChartData(formattedData);
      } else {
        console.error('Failed to fetch chart data or invalid data structure:', res);
        setChartData([]);
      }
      settle();
    });

    let bodyLimit = {
      limit: 3,
      ...eventCategory,
    };

    if(client_id === "1316") {
      bodyLimit.location_id = Number(location);
    }

    if (property !== null) {
      bodyLimit.property_id = Number(property);
    }

    getClientAudits(bodyLimit, (auditResponse) => {
      if (stale) return settle();
      // the api answers with a null body when the window holds no events
      const auditList = Array.isArray(auditResponse?.data) ? auditResponse.data : [];
      setResentEvents(auditResponse?.status === 200 ? auditList : []);
      settle();
    });

    return () => { stale = true; };
  }, [timezoneReady, toDate, location, property, siteType]);

  const eventTypesInitial = [
    //   { Type: 'Camera Alignment', Icon: 'https://storage.vdointel.ai/storage/Images/event_types_icons/camera_alignment.png', Count: 4 },
    //   { Type: 'Dog Of Leash', Icon: 'https://storage.vdointel.ai/storage/Images/event_types_icons/dog_of_leash.png', Count: 8 },
    //   { Type: 'Emergency/Commercial vehicles', Icon: 'https://storage.vdointel.ai/storage/Images/event_types_icons/emergency_vehicles.png', Count: 2 },
    //   { Type: 'Fence Jumping', Icon: 'https://storage.vdointel.ai/storage/Images/event_types_icons/fence_jumping.png', Count: 5 },
    //   { Type: 'Fire/Smoke', Icon: 'https://storage.vdointel.ai/storage/Images/event_types_icons/fire_smoke.png', Count: 1 },
    //   { Type: 'Protocol Violation', Icon: 'https://storage.vdointel.ai/storage/Images/event_types_icons/protocol_violation.png', Count: 3 },
    //   { Type: 'Protocol Violation-Residents', Icon: 'https://storage.vdointel.ai/storage/Images/event_types_icons/protocol_violation_residents.png', Count: 6 },
    //   { Type: 'Protocol Violation-Workers', Icon: 'https://storage.vdointel.ai/storage/Images/event_types_icons/protocol_violation_workers.png', Count: 4 },
    //   { Type: 'Unauthorized usage of Dumpster', Icon: 'https://storage.vdointel.ai/storage/Images/event_types_icons/dumpster.png', Count: 2 },
    // 
  ];

  const [eventTypes, setEventTypes] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(true);

  function adjustCount(baseCount, filter) {
    switch (filter) {
      case '60 min': return Math.max(0, Math.round(baseCount * 0.3 + Math.random() * 2));
      case '24 hrs': return Math.max(0, Math.round(baseCount * 0.6 + Math.random() * 3));
      case 'last 7 days': return Math.max(0, Math.round(baseCount + Math.random() * 5));
      case 'last 30 days': return Math.max(0, Math.round(baseCount * 3 + Math.random() * 10));
      default: return baseCount;
    }
  }

  const filteredEventTypes = eventTypes.map((et) => ({
    ...et,
    Count: adjustCount(et.Count, activeFilter),
  })).filter((et) => et.Count > 0);



  //const activeEventTypeCount = eventTypes[activeEventTypeIndex]?.Count || 0;
  const activeEventTypeCount = getCountByType(eventName);

  function generateChartData(filter, eventCount) {

    switch (filter) {
      case '60 min': return Array.from({ length: 7 }, (_, i) => ({ name: `${i * 10} min`, High: Math.round(eventCount / 3 + Math.random() * 2), Normal: Math.round(eventCount / 2 + Math.random() * 2) }));
      case '24 hrs': return Array.from({ length: 12 }, (_, i) => ({ name: `${String(i * 2).padStart(2, '0')}:00`, High: Math.round(eventCount / 4 + Math.random() * 3), Normal: Math.round(eventCount / 3 + Math.random() * 3) }));
      case 'last 7 days': return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => ({ name: day, High: Math.round(eventCount / 3 + Math.random() * 4), Normal: Math.round(eventCount / 2 + Math.random() * 4) }));
      case 'last 30 days': return [1, 5, 10, 15, 20, 25, 30].map((d) => ({ name: `${d}`, High: Math.round(eventCount / 5 + Math.random() * 5), Normal: Math.round(eventCount / 4 + Math.random() * 5) }));
      default: return [];
    }
  }

  //const chartData = generateChartData(activeFilter, activeEventTypeCount);
  //const chartData = generateChartData(activeFilter, filteredEventTypes[activeEventTypeIndex]?.Type);
  const buttonBackground = (i) => ('#f0f0f0');
  const buttonColor = (i) => ('#5c7fff');

  // const recentEvents = [
  //   { time: '10:30 AM', description: 'Unauthorized Dumpster usage', type: 'Alert', camera: 'Cam A', preview: 'https://via.placeholder.com/60' },
  //   { time: '11:00 AM', description: 'Fence Jumping detected', type: 'Warning', camera: 'Cam B', preview: 'https://via.placeholder.com/60' },
  //   { time: '11:30 AM', description: 'Dog off leash spotted', type: 'Alert', camera: 'Cam C', preview: 'https://via.placeholder.com/60' },
  // ];




  function getCountByType(type) {
    const events = eventTypes.filter((item) => item.Type.toLowerCase() === type.toLowerCase());
    return events.length > 0 ? events[0].Count : 0;
  }


  const handleCheckboxChange = (event) => {
    setConfirmation(true)

    setEscStatus(event.target.checked);

  }

  const closePopUp = () => {

    // console.log(escStatus)
    if (!escStatus) {
      setEscStatus(true)
    }
    else {
      setEscStatus(false)
    }
    setConfirmation(false)
  }

  const changeStatus = () => {
    //console.log(escStatus)
    const isChecked = escStatus === true ? 1 : 0;

    let body = { ClientId: parseInt(client_id, 10), EscalationStatus: isChecked, Type: 'vidisky' }

    InsertUpdateEscStatus(body, (response) => {
      // console.log(response);
      setConfirmation(false)
    })
  }
  //   const nav = useNavigate();
  //   const sendData  = { name: "John", age: 30 };
  // const handleNavigate = () =>{
  //    nav("/escalated", { state: sendData  });
  // }

  //const state = 



  if (dashboardLoading) {
    return (
      <Page title="Dashboard">
        <Box
          sx={{
            minHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Loading dashboard...
          </Typography>
        </Box>
      </Page>
    );
  }

  return (
    <Page title="Dashboard">



      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
        sx={{ px: 3 }}
      >
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
      </Stack>


      <Box sx={{ backgroundColor: (theme) => theme.palette.background.default, minHeight: '100vh', p: 3 }}>
        {client_id === "1316" && locations.length > 1 &&
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={location || ''}
                  label="Location"
                  onChange={(e) => {
                    const val = e.target.value;
                    setLocation(val);
                    localStorage.setItem('selectedLocation', val);
                  }}
                >
                  {locations.map((event) => (
                    <MenuItem key={event.Id} value={event.Id}>
                      {event.LocationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

          </Grid>
        }

        {properties.length > 1 &&
         <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={property || ''}
                  label="Location"
                  onChange={(e) => {
                    const val = e.target.value;
                    setProperty(val);
                    localStorage.setItem('selectedProperty', val);
                  }}
                >
                  {properties.map((event) => (
                    <MenuItem key={event.ID} value={event.ID}>
                      {event.Location}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

          </Grid>
        }


        <Grid container spacing={2} justifyContent="flex-start">
          {eventTypes.filter(item => item.Count > 0).map((item, i) => (
            <Grid item key={i} sx={{ width: 300, flex: '0 0 300px', maxWidth: '100%' }}>
              <Card
                sx={{
                  backgroundColor: UI.cardBg,
                  border: `1px solid ${UI.cardBorder}`,
                  borderRadius: 3,
                  width: 300,
                  maxWidth: '100%',
                  height: 100,
                  p: 2,
                  boxShadow: UI.shadow,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      backgroundColor: UI.accent,
                      border: `2px solid ${UI.accentBorder}`,
                      transform: 'translateY(-3px)',
                      boxShadow: UI.shadowHover,
                      '& .card-text, .card-count': {
                        color: UI.accentText,
                      },
                    '& .card-icon': {
                      filter: 'brightness(0) invert(1)',
                      opacity: 0.8,
                    },
                  },
                }}
                onClick={() => {
                  setEventName(item.Type);
                  setActiveEventTypeIndex(i);
                  navigate('/escalatedEventsUS', {
                    state: { filterData: { fromDate, toDate, eventType: item.Type, location_id : location } },
                  });
                }}
              >
                {/* Count */}
                <Typography
                  variant="h4"
                  className="card-count"
                  sx={{
                    fontWeight: 'bold',
                    color: UI.textMain,
                    zIndex: 2,
                  }}
                >
                  {item.Count}
                </Typography>

                {/* Label */}
                <Typography
                  variant="subtitle2"
                  className="card-text"
                  sx={{
                    color: UI.textMain,
                    zIndex: 2,
                  }}
                >
                  {item.Type}
                </Typography>

                {/* Icon */}
                <Box
                  component="img"
                  src={item.Icon}
                  alt={item.Type}
                  className="card-icon"
                  sx={{
                    position: 'absolute',
                    right: 10,
                    bottom: 10,
                    width: 60,
                    height: 60,
                    opacity: 0.75, // Increased for better visibility
                    filter: 'brightness(0) saturate(100%) invert(30%) sepia(20%) saturate(600%) hue-rotate(190deg) brightness(100%) contrast(95%)', // Slightly brighter navy tone
                    zIndex: 1,
                    transition: 'filter 0.3s ease-in-out, opacity 0.3s ease-in-out',
                  }}
                />

              </Card>
            </Grid>


          ))}
        </Grid>

        <Typography variant="subtitle1" color="primary.text" mb={1} mt={2}>
          View Events From
        </Typography>
        {/* <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mb: 4 }}>
        {timeFilters.map((filter) => (
          <Button
            key={filter}
            // onClick={() => {
            //   setActiveFilter(filter);
            //   setActiveEventTypeIndex(0);
            // }}
            onClick={() => handleTimeFilterClick(filter)}
            sx={{
              backgroundColor: activeFilter === filter ? '#003c8f' : '#e0e0e0',
              color: activeFilter === filter ? '#ffffff' : '#003c8f',
              borderRadius: 2,
              px: 3,
            }}
          >
            {filter}
          </Button>
        ))}
      </Box> */}



        <Grid container alignItems="center" justifyContent="space-between" mb={4}>
          <Grid item>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {timeFilters.map((filter) => (
                <Button
                  key={filter}
                  onClick={() => handleTimeFilterClick(filter)}
                  sx={{
                    backgroundColor: activeFilter === filter ? UI.accent : UI.cardBg,
                    color: activeFilter === filter ? UI.accentText : UI.textMain,
                    border: activeFilter === filter
                      ? `2px solid ${UI.accentBorder}`
                      : `1px solid ${UI.cardBorder}`,
                    borderRadius: '10px',
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    boxShadow: activeFilter === filter
                      ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                      : UI.shadow,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: UI.shadowHover,
                    },
                  }}
                >
                  {filter}
                </Button>
              ))}
            </Box>

          </Grid>

          {/* <Grid item>
      <Box sx={{ display: 'flex'}}>
<FormControlLabel
          control={
            <Checkbox 
              checked={escStatus} 
              onChange={handleCheckboxChange} 
              sx={{ color: '#e0e0e0', '&.Mui-checked': { color: '#90caf9' } }} 
            />
          }
          label="Escalation Notification Required"
          sx={{ color: '#e0e0e0' }} // light label text for dark background
        />

        <MuiTooltip
          title={
            <div>
              <Typography variant="body2" sx={{ fontSize: "0.9rem", whiteSpace: "nowrap" }}>
                <strong>Check:</strong> <span style={{ color: 'green' }}>You will get the escalations</span>
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.9rem", whiteSpace: "nowrap" }}>
                <strong>Uncheck:</strong> <span style={{ color: 'red' }}>Escalation will pause until checked</span>
              </Typography>
            </div>
          }
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: (theme) => theme.palette.background.paper,
                color: '#fff',
                border: '1px solid grey',
                boxShadow: 1,
                padding: '8px',
                whiteSpace: 'normal',
                maxWidth: '350px',
                wordBreak: 'break-word',
              },
            },
          }}
        >
      <Box sx={{ cursor: 'pointer',mt:1 }}>
        <InfoIcon sx={{ color: 'grey' }} />
      </Box>
      </MuiTooltip>
    </Box>
    </Grid> */}
        </Grid>


        <Typography variant="h6" mb={2} color="primary.text">
          Event Trends
        </Typography>

        <Card
          sx={{
            mt: 1,
            backgroundColor: UI.cardBg,
            border: `1px solid ${UI.cardBorder}`,
            borderRadius: 3,
            boxShadow: UI.shadow,
          }}
        >
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <XAxis
                  dataKey="name"
                  stroke={UI.textMuted}
                  tick={{ fill: UI.textMuted }}
                />
                <YAxis
                  domain={isAllZero ? [0, 5] : [0, dataMax => Math.ceil(dataMax)]}
                  ticks={isAllZero ? [0, 1, 2, 3, 4, 5] : undefined}
                  stroke={UI.textMuted}
                  tick={{ fill: UI.textMuted }}
                  tickFormatter={(value) => Math.floor(value).toString()}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: UI.surface, border: "none", color: UI.textMain }}
                  itemStyle={{ color: UI.textMain }}
                  labelStyle={{ color: UI.textMain }}
                />
                <Legend
                  wrapperStyle={{ color: UI.textMuted }}
                />
                <Line type="monotone" dataKey="Previous" stroke="#EF5350" />
                <Line type="monotone" dataKey="Current" stroke="#66BB6A" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>




        <Grid
          container
          spacing={3}
          mt={4}
          sx={{
            display: 'flex',
            alignItems: 'stretch', // Ensure Grid items stretch to the same height
          }}
        >

          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              //minHeight: '400px', 
            }}
          >
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography variant="h6" color="primary.text">Recent Events</Typography>



              <Button
                onClick={() => navigate('/escalatedEventsUS', { state: { filterData: { fromDate, toDate } } })}
                variant="outlined"
                size="small"
                sx={{
                  color: UI.textMain,
                  borderColor: UI.cardBorder,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.12 : 0.06),
                    borderColor: UI.cardBorder,
                  },
                }}
              >
                Show All
              </Button>
            </Grid>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {recentEvents.map((event, index) => (
                <Card
                  key={index}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    cursor: 'pointer',
                    backgroundColor: UI.cardBg,
                    color: UI.textMain,
                    '&:hover': { backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.16 : 0.08) },
                  }}
                  onClick={() =>
                    navigate('/escalatedEventsUS', { state: { filterData: { event } } })
                  }
                >
                  <CardContent
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      width: '100%',
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      src={event.SnapshotUrl}
                      sx={{ width: 60, height: 60, flexShrink: 0 }}
                    />
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                      <Typography variant="subtitle2" sx={{ color: UI.textMain }}>
                        {event.devicename}
                      </Typography>
                      <Typography variant="body2" sx={{ color: UI.textMain }}>
                        {event.DetectedEventType}
                      </Typography>
                      <Typography variant="caption" sx={{ color: UI.textMuted }}>
                        {event.EventTime ? formatEventTime(event.EventTime) : ''} | {event.EventType}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: event.Priority === 'Alert' ? theme.palette.error.main : theme.palette.success.main,
                        flexShrink: 0,
                      }}
                    />
                  </CardContent>
                </Card>
              ))}
            </Box>

          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '400px',
            }}
          >
            <Typography variant="h6" sx={{ color: UI.textMain }}>
              Cameras
            </Typography>
            <Card
              sx={{
                mt: 2,
                flexGrow: 1,
                backgroundColor: UI.cardBg,
                color: UI.textMain,
              }}
            >
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

                <Typography variant="body1" sx={{ color: 'success.main' }}>
                  {/* {client_id === '1266' ? 19 : cameraInfo.active} Online */}
                  {cameraInfo.active} Online
                </Typography>
                <Typography variant="body1" sx={{ color: 'error.main' }}>
                  {cameraInfo.inactive} Offline
                </Typography>

                <Divider sx={{ my: 2, backgroundColor: UI.cardBorder }} />
                <Typography variant="subtitle1" sx={{ color: UI.textMain }}>
                  Cameras by Events
                </Typography>
                {topCameras.map((cam, idx) => (
                  <Typography key={idx} variant="body2" sx={{ mt: 1, color: UI.textMuted }}>
                      {cam.devicename} - {cam.count} Events
                  </Typography>
                ))}
                {/* <Box sx={{ mt: 'auto', pt: 2 }}>
                  <Button variant="contained" color="primary" onClick={() => navigate('/live')}>
                    Go to Live
                  </Button>
                </Box> */}
                <Box sx={{ mt: 'auto', pt: 2 }}>
                  <MuiTooltip
                    title={
                      client_id === "1422"
                        ? "This feature is not available for this client."
                        : ""
                    }
                    arrow
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={client_id === "1422"}
                        onClick={() => {
                          if (client_id !== "1422") {
                            navigate('/live');
                          }
                        }}
                      >
                        Go to Live
                      </Button>
                    </span>
                  </MuiTooltip>
                </Box>
              </CardContent>
            </Card>
          </Grid>

        </Grid>

        <Dialog open={confirmation} sx={{ maxWidth: '90vw', width: '90vw' }}>
          <DialogTitle sx={{ textAlign: 'center' }}> Escalation Update </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            Are you sure you want to change Status of Escalation Notification Required?
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={changeStatus}>
              Yes
            </Button>
            <Button variant="contained" color="error" onClick={closePopUp}>
              No
            </Button>
          </DialogActions>
        </Dialog>


      </Box>
    </Page>
  );
}
